<template>
    <div v-if="isSplitRent">
        <template v-if="splitRentVisible">
            <el-form-item
                :label="field.label"
                :rules="{ required: isFieldRequired, validator: validateField, trigger: 'change' }"
                :prop="fieldProp(field)"
            >
                <span slot="label">
                    {{ field.label }}
                    <span v-if="!_.isEmpty(field.content)" class="fin-relative fin-z-50 fin-ml-1"
                        :class="isFrontend ? 'fin-hidden' : 'fin-hidden 2xl:fin-inline-flex'">
                        <el-popover
                            placement="top-start"
                            :title="field.label"
                            width="200"
                            trigger="hover"
                            :content="field.content"
                            :disabled="_.isEmpty(field.content)"
                            popper-class="app-form-field-popover"
                            >
                            <i slot="reference" class="fas fa-circle-info fin-ml-2 fin-text-warning"></i>
                        </el-popover>
                    </span>
                </span>

                <div v-if="!_.isEmpty(field.content)" class="content fin-flex fin-w-full"
                    :class="isFrontend ? '' : '2xl:fin-hidden'">
                    {{ field.content }}
                </div>

                <div class="fin-w-full">
                    <div class="fin-grid fin-grid-cols-1 md:fin-grid-cols-3 fin-grid-flow-row fin-gap-x-5">
                        <el-form-item
                            v-for="(applicant, applicantIndex) in formData.application.applicants"
                            :key="applicantIndex"
                            :label="`${_.get(applicant, 'first_name', '')} ${_.get(applicant, 'last_name', '')}'s Share`"
                        >
                            <el-input
                                :value="_.get(inputValue, `applicant-${applicantIndex}`)"
                                @input="
                                    (value) => {
                                        setSplit(value, `applicant-${applicantIndex}`)
                                    }
                                "
                                type="number"
                                class="fin-w-full"
                                :placeholder="applicantIndex == 0 ? '100' : '0'"
                                clearable
                                :disabled="isDisabled"
                            >
                                <div slot="suffix"><i class="fas fa-percent"></i></div>
                            </el-input>
                        </el-form-item>
                        <el-form-item label="Non-applicant's Share">
                            <el-input
                                :value="_.get(inputValue, 'non-applicant', '')"
                                @input="
                                    (value) => {
                                        setSplit(value, 'non-applicant')
                                    }
                                "
                                type="number"
                                class="fin-w-full"
                                placeholder="0"
                                clearable
                                :disabled="isDisabled"
                            >
                                <div slot="suffix"><i class="fas fa-percent"></i></div>
                            </el-input>
                        </el-form-item>
                    </div>
                </div>
            </el-form-item>
        </template>
    </div>
    <div v-else>
        <el-form-item
            :rules="{ required: isFieldRequired, validator: validateField, trigger: 'change' }"
            :label="field.label"
            :prop="fieldProp(field)"
        >
            <span slot="label">
                {{ field.label }}
                <span v-if="!_.isEmpty(field.content)" class="fin-relative fin-z-50 fin-ml-1 fin-hidden 2xl:fin-inline-flex">
                    <el-popover
                        placement="top-start"
                        :title="field.label"
                        width="200"
                        trigger="hover"
                        :content="field.content"
                        :disabled="_.isEmpty(field.content)"
                        popper-class="app-form-field-popover"
                    >
                        <i slot="reference" class="fas fa-circle-info fin-ml-2 fin-text-warning"></i>
                    </el-popover>
                </span>
            </span>

            <div v-if="!_.isEmpty(field.content)" class="content 2xl:fin-hidden fin-flex fin-w-full">
                {{ field.content }}
            </div>

            <el-input
                v-if="!['currency'].includes(field.options.type)"
                type="number"
                class="fin-w-full"
                @focus="setCurrentField(field)"
                v-model="inputValue"
                :placeholder="field.placeholder"
                :disabled="isDisabled"
                clearable
            >
                <div v-if="field.options.prepend" slot="prepend" v-html="field.options.prepend"></div>
                <div v-if="field.options.append" slot="append" v-html="field.options.append"></div>
                <div v-if="field.options.prefix" slot="prefix" v-html="field.options.prefix"></div>
                <div v-if="field.options.suffix" slot="suffix" v-html="field.options.suffix"></div>
            </el-input>

            <el-input
                v-else
                type="text"
                pattern="\d*"
                v-model="inputValue"
                @keyup.stop.native="addCommas"
                @blur="addDecimal"
                @focus="removeDecimal"
                :disabled="isDisabled"
                clearable
            >
                <div v-if="field.options.prepend" slot="prepend" v-html="field.options.prepend"></div>
                <div v-if="field.options.append" slot="append" v-html="field.options.append"></div>
                <div v-if="field.options.prefix" slot="prefix" v-html="field.options.prefix"></div>
                <div v-if="field.options.suffix" slot="suffix" v-html="field.options.suffix"></div>
            </el-input>
        </el-form-item>
    </div>
</template>

<script>
import formMixin from "@tenant/app-form/mixins/formMixin"

export default {
    name: "NumberField",

    mixins: [formMixin],

    props: {
        field: {
            required: true,
            type: [Object, Array],
        },
        fieldKey: {
            required: true,
            type: Number,
        },
        path: {
            required: true,
            type: [String, Number],
        },
        value: {
            required: false,
        },
        formStore: {
            required: false,
            type: String,
            default: "formStore"
        },
    },

    data() {
        return {
            inputValue: null,
            splitRentVisible: true,
        }
    },

    mounted() {
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, this.path)
    },

    computed: {
        isFieldRequired() {
            return this.field.isRequired
        },
        isCurrency() {
            return this.field.options.type == "currency" ? true : false
        },
        isSplitRent() {
            return (
                ["split"].includes(this.field.field_mapping) &&
                ["aai.asset_liabilities", "asset_liabilities", "aai.liabilities", "liability", "rent", "asset_liability"].includes(this.field.field_mapping_parent)
            )
        },
        isDisabled() {
            if (this.field.field_mapping_parent == "application") {
                if (this.field.field_mapping == "amount_to_financed") {
                    return true
                }
            }

            if (this.field.field_mapping_parent == "aai.asset_liabilities") {
                let parentPath = this.path.replace(`.${this.field.field_mapping}`, "")
                let parentPathData = _.get(this.formData, parentPath, false)
                if (parentPathData) {
                    if (
                        ["assetValue", "split", "rate", "limit", "balance", "repaymentAmount"].includes(this.field.field_mapping)
                    ) {
                        if (["1", "3"].includes(parentPathData.item)) {
                            return true
                        }
                    }
                }
            }

            if (this.field.field_mapping_parent == "aai.other_income") {
                if (this.field.field_mapping == "value") {
                    let parentPath = this.path.replace(`.${this.field.field_mapping}`, "")
                    let parentPathData = _.get(this.formData, parentPath, false)
                    if (parentPathData && parentPathData.label == "Investment Property Income") {
                        return true
                    }
                }
            }

            return false
        },
    },

    methods: {
        validateField(rule, value, callback) {
            if (["split"].includes(this.field.field_mapping)) {
                let value = Object.values(this.inputValue)
                value = value.filter((value) => value !== "")

                if (_.isEmpty(value)) {
                    if (this.isFieldRequired) {
                        callback(new Error(`${this.field.label} is Required`))
                    } else {
                        callback()
                    }
                } else {
                    let total = 0
                    value.forEach((val) => {
                        total += Number(val)
                    })

                    if (total !== 100) {
                        callback(new Error("The total liability split must be 100% in total, it is currently " + total + "%"))
                    } else {
                        callback()
                    }
                }
            } else {
                if (
                    (!this.inputValue && this.isFieldRequired) ||
                    (this.isCurrency &&
                        this.inputValue &&
                        this.isFieldRequired &&
                        (this.inputValue == "0.00" || this.inputValue == "NaN"))
                ) {
                    callback(new Error(`${this.field.label} is Required`))
                } else {
                    callback()
                }
            }
        },
        addCommas() {
            if (this.inputValue) {
                let inputValueArray = this.inputValue.split(".")
                let value = this.inputValue.replace(/,/g, "")

                let firstDecimalZero = false
                if (_.size(inputValueArray) > 1) {
                    firstDecimalZero = _.size(inputValueArray[1]) == 1 && inputValueArray[1] == "0"
                }

                if (!["."].includes(event.key) && !firstDecimalZero) {
                    this.inputValue = new Intl.NumberFormat("en-US").format(value)
                }
            }
        },
        addDecimal() {
            if (this.inputValue) {
                let value = String(this.inputValue).replace(/,/g, "")
                if (this.isCurrency) {
                    value = new Intl.NumberFormat("en-US", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    }).format(value)
                }
                this.inputValue = value
            }
        },
        removeDecimal() {
            if (this.inputValue) {
                let value = String(this.inputValue).replace(/,/g, "")
                if (this.isCurrency) {
                    let currencyArray = value.split(".")
                    if (currencyArray[1] == "00") {
                        this.inputValue = new Intl.NumberFormat("en-US").format(value)
                    }
                }
            }
        },
        setSplit(value, key) {
            Vue.set(this.inputValue, key, value)
        },
    },

    watch: {
        value: {
            handler(value) {
                if (value !== String(this.inputValue).replace(/,/g, "")) {
                    if (this.isCurrency) {
                        if (this.value && this.value !== "NaN") {
                            this.inputValue = new Intl.NumberFormat("en-US", {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                            }).format(value)
                        } else {
                            this.inputValue = ""
                        }
                    } else {
                        if (!_.isNull(this.value) && this.value && this.value !== "NaN") {
                            this.inputValue = value
                        } else {
                            this.inputValue = undefined
                        }
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        inputValue: {
            handler(inputValue) {

                let path = String(this.path).split(".")
                if (["split"].includes(this.field.field_mapping)) {
                    let pathData = inputValue !== "NaN" ? inputValue : ""
                    this.setPathData(path, pathData)
                } else {
                    let pathData = (inputValue = inputValue && typeof inputValue !== "undefined" && inputValue !== "NaN" ? inputValue : "")
                    this.setPathData(path, String(pathData).replace(/,/g, ""))
                }

                this.checkRules(this.field, this.path)

                // push the value to income and asset liability for investment property
                switch (this.field.field_mapping_parent) {
                    case "investment_properties":
                        switch (this.field.field_mapping) {
                            case "income":
                                let investmentPropertyPath = this.path.split(".income")[0]
                                let investmentPropertyData = _.get(this.formData, investmentPropertyPath, {})
                                _.set(
                                    investmentPropertyData,
                                    "asset_liability_income.value",
                                    String(inputValue).replace(/,/g, "")
                                )
                                let incomeId = _.get(investmentPropertyData, "asset_liability_income.id")
                                let incomePath = `${this.path.split(".investment_properties.")[0]}.aai.other_income`
                                let incomeData = _.get(this.formData, `${incomePath}.${incomeId}`)
                                _.set(incomeData, "value", String(inputValue).replace(/,/g, ""))
                                break
                        }
                        break
                }

                if (["application"].includes(this.field.field_mapping_parent)) {
                    let parentData = _.get(this.formData, "application")

                    if (["amount", "deposit", "trade", "payout"].includes(this.field.field_mapping)) {
                        let amount = _.get(parentData, "amount", "0.00")
                        let deposit = _.get(parentData, "deposit", "0.00")
                        let trade = _.get(parentData, "trade", "0.00")
                        let payout = _.get(parentData, "payout", "0.00")

                        let amountToFinanced = Number(amount) - Number(deposit) - Number(trade) + Number(payout)

                        _.set(this.formData, "application.amount_to_financed", amountToFinanced)
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler() {
                if (this.field.field_mapping_parent == "rent" && this.field.field_mapping == "split") {
                    let isPreviousAddress = !this.path.includes("aai.addresses.0")
                    let addressPath = this.path.replace(".rent.split", "")
                    let addressData = _.get(this.formData, addressPath, false)
                    let residentialStatusId = addressData.residential_status_id
                    if (addressData) {
                        // if the address is a previous address and not owned outright or mortgaged
                        if ([3, 4, 5, 6].includes(Number(addressData.residential_status_id)) && isPreviousAddress) {
                            this.splitRentVisible = false
                        } else {
                            this.splitRentVisible = true
                        }
                    }
                } else {
                    this.splitRentVisible = true
                }
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>

<style lang="scss" scoped>
.el-form-item :deep {
    .el-form-item__label {
        @apply fin-float-none #{!important};
    }
}

.el-input :deep {
    .el-input__prefix,
    .el-input__suffix {
        @apply fin-min-w-[30px] fin-flex fin-items-center fin-justify-center fin-text-body-light;
    }

    .el-input__clear {
        @apply fin-absolute fin-top-0 fin-right-5 fin-bottom-0 fin-left-auto;
    }

    &.is-disabled {
        .el-input__inner {
            @apply fin-text-body #{!important};
        }
    }

    @apply fin-text-body;
}
</style>
